import { useEffect } from 'react';
import { marketDataRuntime } from '../../data/client/marketDataRuntime';
import type { QuoteField, Timeframe } from '../../data/protocol/types';
import { useBarSeries } from '../../data/stores/chartStore';
import { useQuote, useQuoteUniverse } from '../../data/stores/quoteStore';
import type {
  GraphWindowConfig,
  WindowConfig,
  WindowInstance
} from '../../workspace/types';
import { Select } from '../../ui/Select/Select';
import { PriceCanvas } from './PriceCanvas';

interface GraphWindowProps {
  window: WindowInstance<GraphWindowConfig>;
  onConfigChange: (updater: (config: WindowConfig) => WindowConfig) => void;
}

const timeframes: Timeframe[] = ['1s', '5s', '1m', '5m', '15m'];

const liveQuoteFields: QuoteField[] = [
  'symbol',
  'last',
  'open',
  'change',
  'changePct',
  'direction',
  'time'
];

export function GraphWindow({ window, onConfigChange }: GraphWindowProps) {
  const symbol = window.config.symbol;
  const timeframe = window.config.timeframe;
  const limit = window.config.limit;

  const universe = useQuoteUniverse();
  const quote = useQuote(symbol);
  const bars = useBarSeries(symbol, timeframe);

  useEffect(() => {
    return marketDataRuntime.subscribeQuotes(
      `${window.id}:live-price`,
      [symbol],
      liveQuoteFields
    );
  }, [window.id, symbol]);

  useEffect(() => {
    return marketDataRuntime.subscribeBars(
      window.id,
      symbol,
      timeframe,
      limit
    );
  }, [window.id, symbol, timeframe, limit]);

  return (
    <div className="graph-window window-content">
      <div className="window-toolbar">
        <Select
          label="Symbol"
          value={symbol}
          onChange={(event) =>
            onConfigChange(
              (config) =>
                ({
                  ...config,
                  symbol: event.target.value
                }) as GraphWindowConfig
            )
          }
        >
          {(universe.length ? universe.slice(0, 200) : [symbol]).map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>

        <Select
          label="TF"
          value={timeframe}
          onChange={(event) =>
            onConfigChange(
              (config) =>
                ({
                  ...config,
                  timeframe: event.target.value as Timeframe
                }) as GraphWindowConfig
            )
          }
        >
          {timeframes.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>

        <Select
          label="Bars"
          value={limit}
          onChange={(event) =>
            onConfigChange(
              (config) =>
                ({
                  ...config,
                  limit: Number(event.target.value)
                }) as GraphWindowConfig
            )
          }
        >
          {[120, 260, 400, 800].map((count) => (
            <option key={count} value={count}>
              {count}
            </option>
          ))}
        </Select>

        <span className="toolbar-stat">loaded {bars.length}</span>
      </div>

      <div className="graph-window__chart">
        <PriceCanvas bars={bars} symbol={symbol} quote={quote} />
      </div>
    </div>
  );
}